import React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { FaTimes } from 'react-icons/fa';
import './FestivalDetailModal.css';

const FestivalDetailModal = ({ festival, onClose, selectedLanguage }) => {
  const { t } = useTranslation();

  const getFestivalName = () => {
    if (festival.name instanceof Map) {
      return festival.name.get(selectedLanguage) || festival.name.get('en') || 'Festival';
    }
    return festival.name[selectedLanguage] || festival.name.en || 'Festival';
  };

  const getDescription = () => {
    if (festival.description instanceof Map) {
      return festival.description.get(selectedLanguage) || festival.description.get('en') || '';
    }
    return festival.description?.[selectedLanguage] || festival.description?.en || '';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <FaTimes />
        </button>

        <div className="modal-header">
          <h2>{getFestivalName()}</h2>
          <p className="modal-date">
            {format(new Date(festival.date), 'EEEE, MMMM dd, yyyy')}
          </p>
        </div>

        {festival.images && festival.images.length > 0 && (
          <div className="modal-images">
            {festival.images.map((image, index) => {
              // Helper function to build proper URLs
              const buildUrl = (url) => {
                if (!url) return '';
                // If URL already starts with http/https, return as-is (Cloudinary URLs)
                if (url.startsWith('http://') || url.startsWith('https://')) {
                  return url;
                }
                // Remove /api prefix if it exists
                const cleanPath = url.replace(/^\/api/, '');
                // Prepend baseURL for local server URLs
                return `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}${cleanPath}`;
              };

              return (
                <img
                  key={index}
                  src={buildUrl(image)}
                  alt={`${getFestivalName()} ${index + 1}`}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              );
            })}
          </div>
        )}

        <div className="modal-description">
          <h3>{t('festival_description')}</h3>
          <p>{getDescription()}</p>
        </div>

        {festival.videos && festival.videos.length > 0 && (
          <div className="modal-videos">
            <h3>{t('videos')}</h3>
            <div className="videos-grid">
              {festival.videos.map((video, index) => {
                // Helper function to build proper URLs
                const buildUrl = (url) => {
                  if (!url) return '';
                  // If URL already starts with http/https, return as-is (Cloudinary URLs)
                  if (url.startsWith('http://') || url.startsWith('https://')) {
                    return url;
                  }
                  // Remove /api prefix if it exists
                  const cleanPath = url.replace(/^\/api/, '');
                  // Prepend baseURL for local server URLs
                  return `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}${cleanPath}`;
                };

                return (
                  <video
                    key={index}
                    controls
                    src={buildUrl(video)}
                  >
                    Your browser does not support the video tag.
                  </video>
                );
              })}
            </div>
          </div>
        )}

        {/* Wishes Media Section */}
        {festival.wishesMedia && festival.wishesMedia.length > 0 && (
          <div className="wishes-media-section">
            <h3>{t('wishes_media') || 'Wishes & Media'}</h3>
            <div className="wishes-media-grid">
              {festival.wishesMedia.map((media, index) => (
                <div key={index} className="wishes-media-item">
                  {media.mediaType === 'image' ? (
                    <img
                      src={media.filePath}
                      alt={media.occasionName}
                      className="wishes-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="wishes-video-container">
                      {media.thumbnailPath && (
                        <img
                          src={media.thumbnailPath}
                          alt={media.occasionName}
                          className="video-thumbnail"
                        />
                      )}
                      <video
                        controls
                        src={media.filePath}
                        className="wishes-video"
                      >
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  )}
                  <div className="wishes-media-info">
                    <h4>{media.occasionName}</h4>
                    <p className="media-category">{media.category}</p>
                    {media.tags && media.tags.length > 0 && (
                      <div className="media-tags">
                        {media.tags.map((tag, tagIndex) => (
                          <span key={tagIndex} className="media-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FestivalDetailModal;
